import React, { useEffect, useRef } from "react";

const ESPACIADO = 26; // separación de la malla, en px CSS
const RADIO = 165; // alcance del cursor
const EMPUJE = 26; // cuántos px se aparta como mucho un punto
const SEGUIMIENTO = 0.16; // suavizado del desplazamiento (0-1)
const DECAIMIENTO = 0.972; // apagado por fotograma → deja estela
const QUIETO = 0.02; // por debajo de esto damos el punto por colocado

/**
 * Malla de puntos que reacciona al cursor de dos maneras a la vez:
 * se **encienden** dejando estela y se **apartan** del puntero, volviendo
 * luego a su sitio. El resultado es una burbuja que sigue al ratón.
 *
 * Va en canvas y no en DOM: son del orden de 1.500 puntos y pintarlos como
 * nodos costaría demasiado. El lienzo es decorativo, así que no captura
 * eventos ni aparece en el árbol de accesibilidad.
 *
 * @returns {JSX.Element}
 */
const InteractiveDots = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const contexto = canvas.getContext("2d", { alpha: true });
    const prefiereQuieto = window.matchMedia("(prefers-reduced-motion: reduce)");

    let puntos = [];
    let ancho = 0;
    let alto = 0;
    let animacion = null;
    let raton = { x: -9999, y: -9999, dentro: false };

    const construirMalla = () => {
      const caja = canvas.getBoundingClientRect();
      ancho = caja.width;
      alto = caja.height;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(ancho * dpr);
      canvas.height = Math.floor(alto * dpr);
      contexto.setTransform(dpr, 0, 0, dpr, 0, 0);

      puntos = [];
      const margen = ESPACIADO / 2;
      for (let y = margen; y < alto; y += ESPACIADO) {
        for (let x = margen; x < ancho; x += ESPACIADO) {
          puntos.push({ baseX: x, baseY: y, x, y, energia: 0 });
        }
      }
    };

    const pintar = () => {
      contexto.clearRect(0, 0, ancho, alto);
      let sigueVivo = false;

      for (let i = 0; i < puntos.length; i += 1) {
        const punto = puntos[i];
        let destinoX = punto.baseX;
        let destinoY = punto.baseY;

        if (raton.dentro) {
          const dx = punto.baseX - raton.x;
          const dy = punto.baseY - raton.y;
          const distancia = Math.hypot(dx, dy);

          if (distancia < RADIO && distancia > 0.001) {
            const cercania = 1 - distancia / RADIO;
            const intensidad = cercania * cercania;

            // Se aparta del cursor…
            const empuje = intensidad * EMPUJE;
            destinoX = punto.baseX + (dx / distancia) * empuje;
            destinoY = punto.baseY + (dy / distancia) * empuje;

            // …y se enciende.
            if (intensidad > punto.energia) {
              punto.energia = intensidad;
            }
          }
        }

        // Desplazamiento suavizado: se aparta y vuelve solo a su sitio.
        punto.x += (destinoX - punto.x) * SEGUIMIENTO;
        punto.y += (destinoY - punto.y) * SEGUIMIENTO;

        if (punto.energia > 0.002) {
          punto.energia *= DECAIMIENTO;
          sigueVivo = true;
        } else {
          punto.energia = 0;
        }

        if (!sigueVivo) {
          const desviado = Math.abs(punto.x - punto.baseX) + Math.abs(punto.y - punto.baseY);
          if (desviado > QUIETO) {
            sigueVivo = true;
          }
        }

        const e = punto.energia;
        const radio = 1.15 + e * 2.3;
        const alfa = 0.14 + e * 0.82;
        const rojo = Math.round(e * 110);
        const verde = Math.round(104 + e * 110);

        contexto.beginPath();
        contexto.arc(punto.x, punto.y, radio, 0, Math.PI * 2);
        contexto.fillStyle = `rgba(${rojo},${verde},255,${alfa})`;
        contexto.fill();
      }

      if (raton.dentro || sigueVivo) {
        animacion = requestAnimationFrame(pintar);
      } else {
        animacion = null;
      }
    };

    const arrancar = () => {
      if (animacion === null) {
        animacion = requestAnimationFrame(pintar);
      }
    };

    const alMover = (evento) => {
      const caja = canvas.getBoundingClientRect();
      const x = evento.clientX - caja.left;
      const y = evento.clientY - caja.top;
      // Margen extra para que la burbuja entre y salga sin dar un salto.
      const dentro = x > -RADIO && y > -RADIO && x < caja.width + RADIO && y < caja.height + RADIO;
      const estaba = raton.dentro;
      raton = { x, y, dentro };

      // Con dos lienzos en la página (hero y pie), el ratón solo está sobre uno.
      // Sin este corte, mover el ratón en cualquier parte repintaría los dos.
      if (dentro || estaba) {
        arrancar();
      }
    };

    // En una pantalla táctil no hay cursor: la malla responde al dedo mientras
    // se arrastra, y se apaga al levantarlo.
    const alTocar = (evento) => {
      const dedo = evento.touches && evento.touches[0];
      if (!dedo) {
        return;
      }
      const caja = canvas.getBoundingClientRect();
      const x = dedo.clientX - caja.left;
      const y = dedo.clientY - caja.top;
      const dentro = x > -RADIO && y > -RADIO && x < caja.width + RADIO && y < caja.height + RADIO;
      const estaba = raton.dentro;
      raton = { x, y, dentro };
      if (dentro || estaba) {
        arrancar();
      }
    };

    const alSoltar = () => {
      raton.dentro = false;
      arrancar();
    };

    const alSalirDeLaVentana = (evento) => {
      // `mouseout` burbujea desde cualquier hijo: solo cuenta si el puntero
      // abandonó de verdad la ventana (entonces no hay elemento de destino).
      if (evento.relatedTarget === null) {
        raton.dentro = false;
        arrancar(); // la estela se apaga y los puntos vuelven a su sitio
      }
    };

    const alRedimensionar = () => {
      construirMalla();
      arrancar();
    };

    construirMalla();

    if (prefiereQuieto.matches) {
      // Sin interacción: se pinta la malla en reposo y no se registra nada.
      pintar();
      return undefined;
    }

    pintar();
    window.addEventListener("mousemove", alMover, { passive: true });
    window.addEventListener("mouseout", alSalirDeLaVentana, { passive: true });
    window.addEventListener("touchstart", alTocar, { passive: true });
    window.addEventListener("touchmove", alTocar, { passive: true });
    window.addEventListener("touchend", alSoltar, { passive: true });
    window.addEventListener("touchcancel", alSoltar, { passive: true });
    window.addEventListener("resize", alRedimensionar);

    // La sección cambia de alto cuando entran las fuentes web y se recolocan
    // los titulares. Sin esto el lienzo se queda estirado y el cursor deja de
    // coincidir con los puntos.
    let observador = null;
    if (typeof ResizeObserver !== "undefined") {
      observador = new ResizeObserver(alRedimensionar);
      observador.observe(canvas);
    }

    return () => {
      if (animacion !== null) {
        cancelAnimationFrame(animacion);
      }
      if (observador) {
        observador.disconnect();
      }
      window.removeEventListener("mousemove", alMover);
      window.removeEventListener("mouseout", alSalirDeLaVentana);
      window.removeEventListener("touchstart", alTocar);
      window.removeEventListener("touchmove", alTocar);
      window.removeEventListener("touchend", alSoltar);
      window.removeEventListener("touchcancel", alSoltar);
      window.removeEventListener("resize", alRedimensionar);
    };
  }, []);

  return (
    <canvas
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      ref={canvasRef}
    />
  );
};

export default InteractiveDots;
